import BaseRegulation, {BaseRegulationParams, IAccountData, StartSessionTimerContext} from "../base_regulation";
import TempAccountMessageRegulationHelper from "../../helpers/temp_account_message_helper";
import Event from "../../../../utils/event";
import {SYSTEM_EVENTS, CLIENT_EVENTS} from "../../../../events/events";
import _ from "../../../../utils/underscore";
import GenericRealityCheckComponent
    from "../../../../components/regulation_components/helpers/reality_check/generic_reality_check_component";
import {GNWP_SOURCES} from "../../../../../../app-react/services/gnwp/gnwp-consts";
import {CyClass} from "../../../../../../common/cy-classes";
import {GermanyResponsibleGamingButtonPlacement} from "../../../../../../common/const/responsible-gaming";
import {BUTTON_TYPE} from "../../../../components/message/message_consts";
import {DISPLAY_MESSAGE_IDS, DISPLAY_MESSAGE_NAMES} from "../../../../../../common/const/casinofied-analytics-consts";
import GenericRealityCheckMessageHandler from "../../helpers/generic_reality_check_message_handler";
import RealityCheckWithConfirmationMessageHandler from "../../helpers/reality_check_with_confirmation_message_handler";
import { REGULATION_STATUS_ID_MAP } from "../../regulation_const";

interface GermanyRegulationParams extends BaseRegulationParams {
    tempAccountMessageHelper?: TempAccountMessageRegulationHelper,
    placement?:GermanyResponsibleGamingButtonPlacement
}

interface IRealityCheckMessageConfig {
    title: string;
    text?: string;
    closeButton: boolean;
    buttons: [
        {
            name: string;
            isClosable: boolean;
            cssClass?: string;
            callback: () => void;
            type?: string;
        }
    ]
}

export default class GermanyRegulation extends BaseRegulation {
    static CLASS = 'GermanyRegulation';

    init(params: GermanyRegulationParams) {
        super.init(params);
    }

    public getTempAccountGnwpSource() {
        return GNWP_SOURCES.DOCUMENT_UPLOAD;
    }

    showResponsibleIcons() {
        return this.serviceFactory.oAuth2Service().isAuthorized();
    }

    realityCheckEnabled() {
        return true;
    }

    sessionTimerEnabled() {
        return true;
    }

    isAnonymousPlayAllowed() : boolean {
        if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return false;
        }

        return this._getAccountData().RegulationStatusId === REGULATION_STATUS_ID_MAP.APPROVED_FOR_REGULATION;
    }

    async showLoginMessage(): Promise<void> {
        if (this._IsDocUploadRequired()) {
            await this.serviceFactory.externalLinksProxyService().openUploadDocumentFromMyAccount();
        }
    }

    private _IsDocUploadRequired(){
        const accountData = this.serviceFactory.userService().getAccountData();
        return accountData.IsDocUploadRequired;
    }

    protected _getRealityCheckMessageHandler(context: StartSessionTimerContext): GenericRealityCheckMessageHandler {
        return RealityCheckWithConfirmationMessageHandler.create({
            serviceFactory: this.serviceFactory,
            realityCheckInterval: this.getRealityCheckInterval(),
            handleRealityCheckMessageCallback: this._handleRealityCheckMessage.bind(this),
            context: context
        });
    }

    /**
     * Overrides base class
     * @param sessionTimer
     * @private
     */
    async _handleRealityCheckMessage(sessionTimer, confirmCallback:Function): Promise<void> {
        const continueGAObj = {
            category: 'reality_check_message',
            action: 'continue_in_lobby'
        };

        const businessCorrelationId = _.getGuid();

        const languageService = this.serviceFactory.languageService();
        const messageConfig: IRealityCheckMessageConfig = {
            title: languageService.translation('reality_check_title'),
            closeButton: false,
            buttons: [{
                name: languageService.translation('reality_check_continue_btn'),
                cssClass: CyClass("cy-reality-check-close-button"),
                isClosable: true,
                callback: () => {
                    this.serviceFactory.gameService().continuePlayCurrentGame();
                    Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, continueGAObj);

                    this.serviceFactory.casinofiedAnalyticsProxyService().messageActionTaken({
                        businessCorrelationID: businessCorrelationId,
                        displayMessageName: DISPLAY_MESSAGE_NAMES.REALITY_CHECK_GERMANY_MESSAGE_NAME,
                        actionType: "Confirmation"
                    });
                    Event.fire(CLIENT_EVENTS.REALITY_CHECK_CLOSED);
                    confirmCallback();
                }
            }]
        };

        // Add close game button
        if (await this.serviceFactory.gameService().isGameRunning()) {
            if (!await this._realityCheckShouldBeDisplayedInGame()) {
                return;
            }

            continueGAObj.action = 'continue_in_game';

            messageConfig.buttons.unshift({
                name: languageService.translation('close_game'),
                type: BUTTON_TYPE.SECONDARY.typeName,
                isClosable: true,
                callback: () => {
                    Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, {
                        category: 'reality_check_message',
                        action: 'close_game'
                    });

                    this.serviceFactory.casinofiedAnalyticsProxyService().messageActionTaken({
                       businessCorrelationID: businessCorrelationId,
                       displayMessageName: DISPLAY_MESSAGE_NAMES.REALITY_CHECK_GERMANY_MESSAGE_NAME,
                       actionType: "Confirmation"
                    });

                    Event.fire(CLIENT_EVENTS.REALITY_CHECK_CLOSED);
                    this.serviceFactory.gameService().closeRunningGame();
                    confirmCallback();
                }
            });
        }

        this.serviceFactory
            .userService()
            .getRealityCheckData()
            .then(data => {
                Event.fire(CLIENT_EVENTS.REALITY_CHECK_OPENED);

                this.serviceFactory.casinofiedAnalyticsProxyService().messageDisplay({
                    displayMessageID: DISPLAY_MESSAGE_IDS.realityCheckGermany,
                    displayMessageName: DISPLAY_MESSAGE_NAMES.REALITY_CHECK_GERMANY_MESSAGE_NAME,
                    businessCorrelationID: businessCorrelationId
                });

                messageConfig.text = this._getRealityCheckMessageText(data);
                this._openRealityCheckMessage(messageConfig);
            })
            .catch(() => {
                messageConfig.text = this._getRealityCheckMessageText();
                this._openRealityCheckMessage(messageConfig);
            });
    }

    async _realityCheckShouldBeDisplayedInGame() {
        return await this.serviceFactory
            .gameService()
            .shouldShowRealityCheckMessageWhileGameIsOpen();
    }

    _openRealityCheckMessage(messageConfig) {
        messageConfig = {
            ...messageConfig,
            showPopupAboveSecondWebViewHybrid: true
        };

        this.serviceFactory.gameService().stopCurrentGameAutoplay();
        this.serviceFactory.messageManagerService().openMessage(messageConfig);
    }


    /**
     * Returns the text displayed in the reality check message according to the received (or not) reality check data
     * @param realityCheckData
     * @returns {String}
     * @private
     */
    _getRealityCheckMessageText(realityCheckData?) {
        let componentConfig,
            component;


        if (_.isNullOrUndefined(realityCheckData)) {
            componentConfig = {
                elapsedTime: this.getCurrentSessionTime(),
                sessionBalance: 0
            }
        } else {
            componentConfig = {
                elapsedTime: this.getCurrentSessionTime(),
                sessionBalance: -1 * realityCheckData.TotalNetLoss
            }
        }

        component = GenericRealityCheckComponent.create(componentConfig);
        component.start();

        return component.getEl();
    }

    isSessionCooldownEnabled(): boolean {
        return true;
    }

    private _getAccountData(): IAccountData {
        return this.serviceFactory.userService().getAccountData() as IAccountData;
    }

    isLoginPopupEnabled(): boolean {
        return true;
    }
}